export declare namespace UserInfoTokenSections {
	interface TriggerState {
		background: string;
		borderColor: string;
		nameColor: string;
		detailColor: string;
		iconColor: string;
		avatarBackground: string;
		avatarColor: string;
		avatarRingColor: string;
	}

	interface Menu {
		width: string;
	}

	interface Trigger {
		borderRadius: string;
		padding: string;
		/** On the header bar: the menu closed, or the trigger shows no name (below 2xl). */
		closed: TriggerState & {
			hoverBackground: string;
		};
		/** The top of the card the menu opens into, while the trigger shows the name (from 2xl up). */
		open: TriggerState;
		avatar: {
			size: string;
			fontSize: string;
			fontWeight: string;
			ringWidth: string;
		};
		name: {
			fontSize: string;
			lineHeight: string;
			fontWeight: string;
		};
		detail: {
			fontSize: string;
			lineHeight: string;
		};
		icon: {
			size: string;
		};
		mobile: {
			avatar: {
				size: string;
				fontSize: string;
			};
		};
	}

	interface Panel {
		background: string;
		borderColor: string;
		borderRadius: string;
		shadow: string;
		zIndex: string;
		mobile: {
			offset: string;
		};
	}

	interface Header {
		padding: string;
		avatar: {
			fontWeight: string;
		};
		name: {
			color: string;
			fontSize: string;
			lineHeight: string;
			fontWeight: string;
		};
		detail: {
			color: string;
			fontSize: string;
			lineHeight: string;
		};
	}

	interface Nav {
		padding: string;
		gap: string;
	}

	interface Item {
		padding: string;
		borderRadius: string;
		color: string;
		fontSize: string;
		lineHeight: string;
		fontWeight: string;
		hoverBackground: string;
		iconColor: string;
		iconSize: string;
	}

	interface SignOut {
		color: string;
		fontWeight: string;
		hoverBackground: string;
	}
}

export type UserInfoTokens = {
	menu: UserInfoTokenSections.Menu;
	trigger: UserInfoTokenSections.Trigger;
	panel: UserInfoTokenSections.Panel;
	header: UserInfoTokenSections.Header;
	nav: UserInfoTokenSections.Nav;
	item: UserInfoTokenSections.Item;
	signOut: UserInfoTokenSections.SignOut;
};

/**
 * The menu's width: the trigger's while it shows the name (from 2xl up, the menu attached below it), and the floating
 * menu's below 2xl. It fits "Parašas" with the "Nenustatytas" badge, the widest menu row.
 */
export const menu: UserInfoTokenSections.Menu = {
	width: '16rem',
};

export const trigger: UserInfoTokenSections.Trigger = {
	/** Half the pill's 36px height, which rounds its ends fully. */
	borderRadius: '1.125rem',
	padding: '0.125rem 0.625rem 0.125rem 0.125rem',
	closed: {
		background: 'transparent',
		borderColor: 'transparent',
		hoverBackground: '{menubar.item.focus.background}',
		nameColor: '{menubar.item.focus.color}',
		detailColor: '{menubar.item.color}',
		iconColor: '{menubar.item.color}',
		avatarBackground: 'transparent',
		avatarColor: '{menubar.item.focus.color}',
		avatarRingColor: '{menubar.item.color}',
	},
	open: {
		background: '{content.background}',
		borderColor: '{content.border.color}',
		nameColor: '{text.color}',
		detailColor: '{surface.400}',
		iconColor: '{text.color}',
		avatarBackground: '{primary.color}',
		avatarColor: '{primary.contrast.color}',
		avatarRingColor: '{primary.color}',
	},
	avatar: {
		size: '1.875rem',
		fontSize: '0.75rem',
		fontWeight: '700',
		ringWidth: '1.5px',
	},
	name: {
		fontSize: '{app.sm.font.size}',
		lineHeight: '1rem',
		fontWeight: '600',
	},
	detail: {
		fontSize: '0.75rem',
		lineHeight: '0.875rem',
	},
	icon: {
		size: '1rem',
	},
	mobile: {
		avatar: {
			size: '2rem',
			fontSize: '0.75rem',
		},
	},
};

export const panel: UserInfoTokenSections.Panel = {
	background: '{content.background}',
	borderColor: '{content.border.color}',
	borderRadius: '{border.radius.xl}',
	shadow: '{overlay.navigation.shadow}',
	zIndex: '1000',
	mobile: {
		offset: '0.5rem',
	},
};

export const header: UserInfoTokenSections.Header = {
	padding: '1.125rem 1.125rem 1rem',
	avatar: {
		fontWeight: '700',
	},
	name: {
		color: '{text.color}',
		fontSize: '0.9375rem',
		lineHeight: '1.375rem',
		fontWeight: '700',
	},
	detail: {
		color: '{surface.400}',
		fontSize: '0.8125rem',
		lineHeight: '1.25rem',
	},
};

export const nav: UserInfoTokenSections.Nav = {
	padding: '0.375rem',
	gap: '0.125rem',
};

export const item: UserInfoTokenSections.Item = {
	padding: '0 0.625rem',
	borderRadius: '{border.radius.lg}',
	color: '{surface.700}',
	fontSize: '{app.sm.font.size}',
	lineHeight: '{app.sm.line.height}',
	fontWeight: '500',
	hoverBackground: '{surface.50}',
	iconColor: '{surface.400}',
	iconSize: '1.125rem',
};

export const signOut: UserInfoTokenSections.SignOut = {
	color: '{red.700}',
	fontWeight: '600',
	hoverBackground: '{red.50}',
};

export default {
	menu,
	trigger,
	panel,
	header,
	nav,
	item,
	signOut,
} satisfies UserInfoTokens;
